/**
 * Tid-synkronisering mot server.
 *
 * Beregner offset mellom klient-klokke og server-klokke basert på flere målinger,
 * tar median for robusthet. Brukes til at alle seilere ser samme nedtelling
 * uavhengig av feilkalibrerte telefoner.
 *
 * Metode:
 *   1. Noter clientBefore
 *   2. Hent /api/time
 *   3. Noter clientAfter
 *   4. RTT = clientAfter - clientBefore
 *   5. Anta at server-klokka ble lest midt i vinduet: midpoint = clientBefore + RTT/2
 *   6. offset = serverEpoch - midpoint
 *   7. now = Date.now() + offset
 */

export interface SyncResult {
  offsetMs: number;
  rttMs: number;
  sampleCount: number;
  worstRttMs: number;
}

interface Sample {
  offset: number;
  rtt: number;
}

export async function syncTime(samples = 5): Promise<SyncResult> {
  const results: Sample[] = [];
  for (let i = 0; i < samples; i++) {
    try {
      const sample = await oneSample();
      results.push(sample);
    } catch (err) {
      console.warn('Tid-sync sample feilet:', err);
    }
    // Liten pause for å unngå å treffe samme tick
    if (i < samples - 1) {
      await new Promise((r) => setTimeout(r, 120));
    }
  }

  if (results.length === 0) {
    return { offsetMs: 0, rttMs: 0, sampleCount: 0, worstRttMs: 0 };
  }

  // Sorter etter RTT og kast bort dårligste halvdel (adaptivt)
  results.sort((a, b) => a.rtt - b.rtt);
  const bestHalf = results.slice(0, Math.max(1, Math.ceil(results.length / 2)));

  // Median offset av bestHalf
  const offsets = bestHalf.map((s) => s.offset).sort((a, b) => a - b);
  const median = offsets[Math.floor(offsets.length / 2)] ?? 0;

  const rtts = bestHalf.map((s) => s.rtt);
  const avgRtt = rtts.reduce((s, x) => s + x, 0) / rtts.length;
  const worstRtt = Math.max(...results.map((s) => s.rtt));

  return {
    offsetMs: Math.round(median),
    rttMs: Math.round(avgRtt),
    sampleCount: results.length,
    worstRttMs: Math.round(worstRtt),
  };
}

async function oneSample(): Promise<Sample> {
  const clientBefore = Date.now();
  const res = await fetch('/api/time', { cache: 'no-store' });
  const clientAfter = Date.now();
  if (!res.ok) throw new Error(`time endpoint ${res.status}`);
  const body = (await res.json()) as { epochMs: number };
  const rtt = clientAfter - clientBefore;
  const midpoint = clientBefore + rtt / 2;
  const offset = body.epochMs - midpoint;
  return { offset, rtt };
}

/**
 * Returner synkronisert "nå" basert på beregnet offset.
 */
export function syncedNow(offsetMs: number): number {
  return Date.now() + offsetMs;
}
